import { useCallback } from 'react';

import { Apollo } from 'src/gql/generated';
import {
  extractFileName,
  useFilestackClient,
  useGetFreshFilestackClient,
} from 'src/helpers/integrations/filestack';
import { multiuploadWithAuthRetry } from 'src/helpers/integrations/filestack-multiupload';
import { parseFilestackUploadPayloads } from 'src/helpers/integrations/filestack-upload-payload';
import { useCreateFiles, useCreateStaticFileVersions } from 'src/hooks';

import { splitFilesByUploadTarget } from './split-files-by-upload-target';
import type { VersionTargetFile } from './split-files-by-upload-target';
import { useDocxUploadDocumentLookup } from './use-docx-upload-document-lookup';
import { useUploadAsLatestVersion } from './use-upload-as-latest-version';

export interface VersionAwareUploadedFile {
  // The matched document for a version upload, or the newly created document.
  documentId: string;
  file: File;
  kind: 'newDocument' | 'version';
  name: string;
}

export interface VersionAwareUploadResult {
  // Files that did not persist as a document or version.
  failedFiles: File[];
  // Persisted references in input order.
  uploadedFiles: VersionAwareUploadedFile[];
}

type CreateFilesRecords = Apollo.CreateFilesMutationVariables['input']['records'];

export interface VersionAwareUploadOptions {
  // Override persistence for unmatched files, such as launch-config intake.
  createNewDocuments?: (
    records: CreateFilesRecords
  ) => Promise<{ documentId: string; name: string }[]>;
  documentVariant?: Apollo.DocumentVariant;
}

/**
 * Upload files, attach confirmed DOCX matches as versions, and create documents
 * for the rest. Consumers must render `versionDialog`.
 */
export function useVersionAwareUpload(): {
  uploadFiles: (
    files: File[],
    options?: VersionAwareUploadOptions
  ) => Promise<VersionAwareUploadResult>;
  versionDialog: JSX.Element;
} {
  const { createFiles } = useCreateFiles();
  const filestackClient = useFilestackClient();
  const lookupDocument = useDocxUploadDocumentLookup();
  const getFreshFilestackClient = useGetFreshFilestackClient();
  const { createStaticFileVersions } = useCreateStaticFileVersions();
  const { confirmUploadAsLatestVersion, dialog: versionDialog } = useUploadAsLatestVersion();

  const uploadFiles = useCallback(
    async (
      files: File[],
      options: VersionAwareUploadOptions = {}
    ): Promise<VersionAwareUploadResult> => {
      const { createNewDocuments, documentVariant = Apollo.DocumentVariant.Document } = options;
      if (!files.length) return { failedFiles: [], uploadedFiles: [] };

      const versionTargetByFile = new Map<File, VersionTargetFile['document']>();
      const { versionTargetFiles } = await splitFilesByUploadTarget({
        confirmUploadAsLatestVersion,
        files,
        lookupDocument,
      });
      for (const { document, file } of versionTargetFiles) versionTargetByFile.set(file, document);

      const uploadResults = await multiuploadWithAuthRetry({
        files,
        filestackClient,
        getFreshFilestackClient,
      });
      const payloads = parseFilestackUploadPayloads(uploadResults, files.length);
      const entries = files.map((file, index) => {
        const payload = payloads.at(index);
        if (!payload) throw new Error('Uploaded files returned unexpected data from storage.');
        return {
          file,
          fileLocation: {
            container: payload.container,
            filename: extractFileName(file.name),
            handle: payload.handle,
            key: payload.key,
            mimetype: payload.mimetype,
            size: payload.size,
            url: payload.url,
          },
        };
      });
      const versionEntries = entries.flatMap(entry => {
        const target = versionTargetByFile.get(entry.file);
        return target ? [{ ...entry, target }] : [];
      });
      const newDocumentEntries = entries.filter(entry => !versionTargetByFile.has(entry.file));

      const uploadedByFile = new Map<File, VersionAwareUploadedFile>();
      const failedFiles: File[] = [];
      if (versionEntries.length) {
        for (const { file, fileLocation, target } of versionEntries) {
          let attached = false;
          try {
            const { data, errors } = await createStaticFileVersions({
              variables: {
                input: {
                  records: [{ documentId: target.id, props: { fileLocation } }],
                },
              },
            });
            attached = !errors?.at(0) && Boolean(data?.createStaticFileVersions?.at(0));
          } catch {
            attached = false;
          }

          if (attached)
            uploadedByFile.set(file, {
              documentId: target.id,
              file,
              kind: 'version',
              name: target.name,
            });
          else failedFiles.push(file);
        }
      }
      if (newDocumentEntries.length) {
        try {
          const records = newDocumentEntries.map(({ fileLocation }) => ({
            documentProps: { isSigned: false, variant: documentVariant },
            fileLocationProps: fileLocation,
          }));
          const created = createNewDocuments
            ? await createNewDocuments(records)
            : await createNewDocumentsViaMutation(createFiles, records);
          newDocumentEntries.forEach((entry, index) => {
            const document = created.at(index);
            if (document)
              uploadedByFile.set(entry.file, {
                documentId: document.documentId,
                file: entry.file,
                kind: 'newDocument',
                name: document.name,
              });
            else failedFiles.push(entry.file);
          });
        } catch {
          for (const { file } of newDocumentEntries) failedFiles.push(file);
        }
      }

      // Restore input order after the two persistence paths.
      const uploadedFiles = files.flatMap(file => {
        const uploaded = uploadedByFile.get(file);
        return uploaded ? [uploaded] : [];
      });
      return { failedFiles, uploadedFiles };
    },
    [
      confirmUploadAsLatestVersion,
      createFiles,
      createStaticFileVersions,
      filestackClient,
      getFreshFilestackClient,
      lookupDocument,
    ]
  );

  return { uploadFiles, versionDialog };
}

async function createNewDocumentsViaMutation(
  createFiles: ReturnType<typeof useCreateFiles>['createFiles'],
  records: CreateFilesRecords
): Promise<{ documentId: string; name: string }[]> {
  const { data } = await createFiles({ variables: { input: { records } } });
  return (data?.createFiles ?? []).map(document => ({
    documentId: document.id,
    name: document.name,
  }));
}
